import { log } from '../../util/log';
import { BookingContext } from '../../util/booking-context';
import { callWp } from '../../util/call-wp';
import { hideLoadingOverlay, showLoadingOverlay } from '../../util/loading-overlay';
import { loadConfirmAppointmentPage } from './confirm-appointment';
import { loadChooseServicesPage } from './choose-services';
import { loadSelectLocationPage } from './select-location';
import { bindSummary, updateSummary } from '../../util/summary';
import { track } from '../../util/tracking';
import { getInjectablesCategoryId } from '../constants/constants';

const PREFETCH_ENABLED = false;


type DateAvailability = 'none' | 'low' | 'medium' | 'high' | 'unknown';

interface ApiBookingSlot {
	time: string;
	displayName: string;
	serviceBookingId: string;
}

interface ApiResponse {
	slots: ApiBookingSlot[];
	error?: string;
	html: string;
	consultData?: any;
}

interface ApiTimeslotResponse {
	slots: ApiBookingSlot[];
	futureDays: Record<string, boolean>;
	error?: string;
}

const ALL_THERAPISTS_KEY = 'all';

export async function loadFindTimeslotPage(context: BookingContext) {
	const dayAvailabilitiesMapWithNurses = new Map<string, Map<string, DateAvailability>>();
	const daySlotsMapWithNurses = new Map<string, Map<string, ApiBookingSlot[]>>();

	dayAvailabilitiesMapWithNurses.set(ALL_THERAPISTS_KEY, new Map<string, DateAvailability>());
	daySlotsMapWithNurses.set(ALL_THERAPISTS_KEY, new Map<string, ApiBookingSlot[]>());

	let dayAvailabilitiesMap = dayAvailabilitiesMapWithNurses.get(ALL_THERAPISTS_KEY);
	let daySlotsMap = daySlotsMapWithNurses.get(ALL_THERAPISTS_KEY);

	let shouldStop = false;
	window.scroll(0, 0);

	showLoadingOverlay(context);
	context.root.innerHTML = '';
	const ssrStartTime = performance.now();

	const depositRequired = context.data.categoryName === getInjectablesCategoryId() && !context.data.reschedule;
	const ssrResponse = await callWp('GET', 'view_find_timeslot', { depositRequired, ...context.data });
	const ssrEndTime = performance.now();
	log('SSR took', ssrEndTime - ssrStartTime, 'ms');

	const { html, error, consultData } = ssrResponse;

	if (error) {
		log('Error loading find timeslot page:', error);
		context.root.innerHTML = error;
		return;
	}

	if (consultData) {
		context.data.consultData = consultData;
	}

	context.root.innerHTML = html;

	bindSummary(context);

	hideLoadingOverlay(context);

	if (!context.data.reschedule && !context.data.book) {
		context.root.querySelector('[data-step="0"]')?.addEventListener('click', () => {
			shouldStop = true;
			loadSelectLocationPage(context);
		});

		context.root.querySelector('[data-step="1"]')?.addEventListener('click', () => {
			shouldStop = true;
			loadChooseServicesPage(context);
		});
	}

	context.root.querySelectorAll<HTMLButtonElement>('button[data-reload]').forEach((b) =>
		b.addEventListener('click', () => {
			shouldStop = true;
			loadFindTimeslotPage(context);
		})
	);

	const nextButton = context.root.querySelector<HTMLButtonElement>('[data-next]');
	const backButton = context.root.querySelector<HTMLButtonElement>('[data-back]');
	if (!nextButton) {
		log('No next button found');
		return;
	}
	// if (!backButton) {
	// 	log('No next button found');
	// 	return;
	// }

	// Calendar
	const calendar = context.root.querySelector<HTMLDivElement>('[data-calendar]');
	if (!calendar) {
		log('No calendar found');
		return;
	}

	const initializeCalendarAvailability = () => {
		const calendarDays = calendar.querySelectorAll<HTMLButtonElement>('[data-calendar-day]');
		calendarDays.forEach((calendarDay) => {
			const date = calendarDay.dataset.date;
			if (!date) return;

			// Only initialize future dates as unknown
			const isInPast = new Date(date) < new Date(new Date().setHours(0, 0, 0, 0));
			if (!isInPast) {
				dayAvailabilitiesMap.set(date, 'unknown');
				calendarDay.classList.add('timeslots__calendar-day--unknown');
			}
		});
	};
	initializeCalendarAvailability();

	const chooseTimeButton = context.root.querySelector<HTMLButtonElement>('[data-choose-time]');
	const earliestTimeButton = context.root.querySelector<HTMLButtonElement>('[data-earliest-time]');
	const timeslotsListSection = context.root.querySelector<HTMLDivElement>('[data-timeslots-list]');

	if (!chooseTimeButton || !earliestTimeButton || !timeslotsListSection || !nextButton) {
		log('Required elements not found');
		return;
	}
	const calendarDays = calendar.querySelectorAll<HTMLButtonElement>('[data-calendar-day]:not(disabled)');

	const rerenderCalendarDays = () => {
		const calendarDaysAgain = calendar.querySelectorAll<HTMLButtonElement>('[data-calendar-day]:not(disabled)');
		// Update the calendar days based on the new availability data
		let delay = 1;
		if (context.data.therapistId && !dayAvailabilitiesMapWithNurses.has(context.data.therapistId)) {
			dayAvailabilitiesMapWithNurses.set(context.data.therapistId, new Map<string, DateAvailability>());
		}

		const currentDayAvailabilitiesMap = dayAvailabilitiesMapWithNurses.get(context.data.therapistId ?? ALL_THERAPISTS_KEY);
		calendarDaysAgain.forEach((calendarDay) => {
			const date = calendarDay.dataset.date;
			if (!date) return;

			const availability = currentDayAvailabilitiesMap.get(date);
			if (availability !== 'unknown') {
				const wasUnknwn = calendarDay.classList.contains('timeslots__calendar-day--unknown');
				calendarDay.classList.remove('timeslots__calendar-day--unknown');
                calendarDay.classList.remove('timeslots__calendar-day--unavailable');

				// Reset classes first
				// calendarDay.disabled = false;

				setTimeout(
					() => {
						// If we know this date has no slots, mark it as unavailable
						if (availability === 'none') {
							calendarDay.classList.add('timeslots__calendar-day--unavailable');
							// calendarDay.disabled = true;
						}

						// If we don't know the availability, mark it as unknown
						// if (availability === 'unknown') {
						// 	calendarDay.classList.add('timeslots__calendar-day--unknown');
						// }

						// if high availability, mark it as high
						if (availability === 'high') {
							calendarDay.classList.add('timeslots__calendar-day--high');
						}
					},
					wasUnknwn ? delay * 300 : 0
				);
				if (wasUnknwn) {
					delay++;
				}
			} else {
				if (!calendarDay.classList.contains('timeslots__calendar-day--disabled')) {
					calendarDay.classList.add('timeslots__calendar-day--unknown');
				}
			}
		});
	};

	const loadSlotsForDate = async (calendarDay, runInBackground = false) => {
		const currentlySelectedNurse = context.data.therapistId ?? ALL_THERAPISTS_KEY;

		if (!dayAvailabilitiesMapWithNurses.has(currentlySelectedNurse)) {
			dayAvailabilitiesMapWithNurses.set(currentlySelectedNurse, new Map<string, DateAvailability>());
		}
		if (!daySlotsMapWithNurses.has(currentlySelectedNurse)) {
			daySlotsMapWithNurses.set(currentlySelectedNurse, new Map<string, ApiBookingSlot[]>());
		}

		const currentDayAvailabilitiesMap = dayAvailabilitiesMapWithNurses.get(currentlySelectedNurse);
		const currentDaySlotsMap = daySlotsMapWithNurses.get(currentlySelectedNurse);

		log('Calendar day clicked');
		const date = calendarDay.dataset.date;
		if (!date) return;

		// Check if we know this date is unavailable
		const availability = currentDayAvailabilitiesMap.get(date);
		if (availability === 'none') {
			return;
		}

		shouldStop = true;
		context.data.date = date;
		if (!runInBackground) {
			calendarDays.forEach((btn) => {
				btn.classList.toggle('timeslots__calendar-day--selected', false);
				btn.disabled = false;
			});

			calendarDay.classList.toggle('timeslots__calendar-day--selected', true);
			// calendarDay.disabled = true;

			updateSummary(context, rerenderRows);

			timeslotsListSection.classList.toggle('hidden', false);
		}

		// Check cache first
		if (currentDaySlotsMap.has(date)) {
			const cachedSlots = currentDaySlotsMap.get(date)!;
			if (cachedSlots.length === 0) {
				calendarDay.classList.add('timeslots__calendar-day--unavailable');
			}
			timeslotsListSection.innerHTML = renderTimeslots(date, cachedSlots);
			rebindTimeslotButtons();
			return;
		}

		// If not in cache, load from server
		if (!runInBackground) {
			// calendarDays.forEach((btn) => (btn.disabled = true));
			timeslotsListSection.innerHTML = loadingSpinner();
		}

		const response = await callWp('GET', 'view_find_timeslot_fragment', context.data);

		if (!runInBackground) {
			// calendarDays.forEach((btn) => (btn.disabled = false));
		}

		if (response.error) {
			track({
				event: 'bookingError',
				message: 'Error loading timeslots',
			});
			return;
		}

		// Cache the results
		currentDaySlotsMap.set(date, response.timeslots);

		// use the future days to update the availability map
		if (response.futureDays) {
			// skip future days
			if (PREFETCH_ENABLED) {
				Object.entries(response.futureDays).forEach(([dateString, hasSlots]) => {
					// Convert the date string to YYYY-MM-DD format
					const date = dateString.split('T')[0];
					currentDayAvailabilitiesMap.set(date, hasSlots ? 'high' : 'none');

					// console.log(date, hasSlots);
				});
			}
		}

		// Update calendar day appearance based on availability
		if (!response.timeslots || response.timeslots.length === 0) {
			// calendarDay.classList.add('timeslots__calendar-day--unavailable');
			if (PREFETCH_ENABLED) {
				currentDayAvailabilitiesMap.set(date, 'none');
			}
		}

		rerenderCalendarDays();

		if (!runInBackground) {
			timeslotsListSection.innerHTML = renderTimeslots(date, response.timeslots);
		}
		rebindTimeslotButtons();
	};
	calendarDays.forEach((calendarDay) => {
		calendarDay.addEventListener('click', async () => {
			loadSlotsForDate(calendarDay);
		});
	});

	const prefetchMonth = async () => {
		// console.log('prefetching month');
		// select the first date of the month that is active
		const activeMonth = calendar.querySelector<HTMLDivElement>('[data-calendar-month]:not(.hidden)');
		if (!activeMonth) {
			// console.log('no active month');
			return;
		}

		const firstActiveDay = activeMonth.querySelector<HTMLButtonElement>('[data-calendar-day]:not(.timeslots__calendar-day--disabled)');
		if (!firstActiveDay) {
			// console.log('no active day');
			return;
		}

		await loadSlotsForDate(firstActiveDay, true);

		if (!PREFETCH_ENABLED) {
			return;
		}

		// Pre-fetch 4 weeks ahead
		let currentDate = new Date(firstActiveDay.dataset.date);
		for (let i = 1; i <= 4; i++) {
			currentDate.setDate(currentDate.getDate() + 7);
			const dateString = currentDate.toISOString().split('T')[0];

			const calendarDay = calendar.querySelector<HTMLButtonElement>(
				`[data-calendar-day][data-date="${dateString}"]:not(.timeslots__calendar-day--disabled)`
			);
			if (!calendarDay) {
				return;
			}
			await loadSlotsForDate(calendarDay, true);
		}
	};

	prefetchMonth();

	// On the calendar next and previous buttons, add an event listener to hide this month element and show the next or previous month element
	const calendarNextButtons = calendar.querySelectorAll<HTMLButtonElement>('[data-calendar-next]');
	calendarNextButtons.forEach((calendarNext) =>
		calendarNext.addEventListener('click', () => {
			log('Calendar next clicked');
			const currentMonth = calendar.querySelector<HTMLElement>('[data-calendar-month]:not(.hidden)');
			if (!currentMonth) {
				return;
			}
			const nextMonth = currentMonth.nextElementSibling as HTMLElement;
			if (!nextMonth) {
				return;
			}
			currentMonth.classList.add('hidden');
			nextMonth.classList.remove('hidden');

			prefetchMonth();
		})
	);
	const calendarPreviousButtons = calendar.querySelectorAll<HTMLButtonElement>('[data-calendar-prev]');
	calendarPreviousButtons.forEach((calendarPrevious) =>
		calendarPrevious.addEventListener('click', () => {
			log('Calendar previous clicked');
			const currentMonth = calendar.querySelector<HTMLElement>('[data-calendar-month]:not(.hidden)');
			if (!currentMonth) {
				return;
			}
			const previousMonth = currentMonth.previousElementSibling as HTMLElement;
			if (!previousMonth) {
				return;
			}

			currentMonth.classList.add('hidden');
			previousMonth.classList.remove('hidden');

			prefetchMonth();
		})
	);

	function renderTimeslots(date: string, slots: ApiBookingSlot[]): string {
		if (!slots || slots.length === 0) {
			return '<div class="spin__container">No available bookings. Please select another date or call your clinic to book.</div>';
		}

		const displayDate = new Date(date).toLocaleDateString('en-US', {
			weekday: 'long',
			day: 'numeric',
			month: 'short',
		});

		return `
            <span class="timeslots__list-item-label">${displayDate}</span>
            ${slots
				.map((slot) => {
					const displayTime = new Date(slot.time).toLocaleTimeString('en-US', {
						hour: 'numeric',
						minute: '2-digit',
						hour12: true,
					});

					return `
                    <button
                        data-slot
                        data-service-booking-id="${slot.serviceBookingId}"
                        data-time="${slot.time}"
                        data-display-date="${new Date(date).toLocaleDateString('en-US', {
							day: 'numeric',
							month: 'long',
							year: 'numeric',
						})}"
                        data-display-time="${displayTime}"
                        class="timeslots__list-item snb-fff"
                    >
                        ${displayTime}
                    </button>
                `;
				})
				.join('')}
        `;
	}

	// Rest of the code (rebindTimeslotButtons, loadEarliest, etc.) remains mostly the same
	// Just update them to use the new renderTimeslots function and caching mechanism

	function rebindTimeslotButtons() {
		const slots = context.root.querySelectorAll<HTMLButtonElement>('button[data-slot]');
		slots.forEach((slot) => {
			slots.forEach((btn) => {
				btn.classList.toggle('timeslots__list-item--active', false);
			});
			if (slot.dataset.serviceBookingId === context.data.serviceBookingId && slot.dataset.time === context.data.slotTime) {
				slot.classList.toggle('timeslots__list-item--active', true);
			}

			slot.addEventListener('click', () => {
				const time = slot.dataset.time;
				const serviceBookingId = slot.dataset.serviceBookingId;
				if (!time || !serviceBookingId) return;

				slots.forEach((btn) => {
					btn.classList.toggle('timeslots__list-item--active', false);
				});

				slot.classList.toggle('timeslots__list-item--active', true);

				context.data.slotTime = time;
				context.data.displayDate = slot.dataset.displayDate ?? null;
				context.data.displayTime = slot.dataset.displayTime ?? null;
				context.data.serviceBookingId = serviceBookingId;

				nextButton.disabled = false;

				updateSummary(context, rerenderRows);
			});
		});
	}

	backButton?.addEventListener('click', async () => {
		shouldStop = true;
		// if resschedule, go back to my account, otherwise go back to choose services
		if (context.data.reschedule) {
			showLoadingOverlay(context);
			window.location.href = silkNexusWp.myAccountUrl + '/appointments';
			return;
		}

		return loadChooseServicesPage(context);
	});

	nextButton.addEventListener('click', async () => {
		shouldStop = true;

		log('Next button clicked');

		if (!context.data.slotTime || !context.data.serviceBookingId) {
			return;
		}

		showLoadingOverlay(context);

		const response = await reserveBooking(context);

		if (response?.error || !response?.success) {
			track({
				event: 'bookingError',
				message: 'Error reserving appointment',
			});
			log('Error reserving appointment:', response.error || 'An unknown error occurred');

			if (!document.getElementById('unexpectedErrorModal')) {
				const modalHTML = `
                    <div class="modal fade" id="unexpectedErrorModal" data-bs-backdrop="static" data-bs-keyboard="false" tabindex="-1" aria-hidden="true">
                        <div class="modal-dialog modal-dialog-centered">
                        <div class="modal-content" style="padding: 2rem; max-width: 28rem">
                            <div class="modal-body mb-3 p-0 d-flex flex-column align-items-center justify-content-center text-center">
                            <svg xmlns="http://www.w3.org/2000/svg" height="2em" viewBox="0 0 512 512" style="margin-bottom: 1rem"><path d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zm0-384c13.3 0 24 10.7 24 24V264c0 13.3-10.7 24-24 24s-24-10.7-24-24V152c0-13.3 10.7-24 24-24zM224 352a32 32 0 1 1 64 0 32 32 0 1 1 -64 0z"/></svg>
                            <span>An unexpected error occurred. Please try again later.</span>
                            </div>
                            <div class="d-flex flex-row gap-2">
                            <button type="button" class="w-100 btn btn-primary text-white" style="font-weight: 700; font-size: 0.875rem">CONTINUE</button>
                            </div>
                        </div>
                        </div>
                    </div>
                    `;
				document.body.insertAdjacentHTML('beforeend', modalHTML);
			}

			const modalElement = document.getElementById('unexpectedErrorModal')!;

			const modal = new window.bootstrap.Modal(modalElement, {
				backdrop: 'static',
				keyboard: false,
			});

			// on next, select clinic
			modalElement.querySelector<HTMLButtonElement>('.btn-primary')!.addEventListener('click', () => {
				modal.hide();
				window.location.href = `${silkNexusWp.myAccountUrl}/appointments`;
			});

			modal.show();
			return;
		}

		await loadConfirmAppointmentPage(context);
	});

	updateSummary(context, rerenderRows);

	function rerenderRows() {
		updateSummary(context, rerenderRows);

		if (context.data.serviceIds.length === 0) {
			loadChooseServicesPage(context);
		}

		// Reload this page if the treatments change
		loadFindTimeslotPage(context);
	}

	chooseTimeButton.addEventListener('click', () => {
		shouldStop = true;
		// show the calendar
		// hide the earliest time slots

		if (context.data.date) {
			timeslotsListSection.classList.toggle('hidden', false);
		}

		chooseTimeButton.classList.toggle('timeslots__button--active', true);

		earliestTimeButton.classList.toggle('timeslots__button--active', false);
	});

	const loadEarliest = async () => {
		if (shouldStop) {
			shouldStop = false;
		}

		timeslotsListSection.classList.toggle('hidden', false);
		chooseTimeButton.classList.toggle('timeslots__button--active', false);
		earliestTimeButton.classList.toggle('timeslots__button--active', true);

		const calendarButtons = Array.from(calendar.querySelectorAll<HTMLButtonElement>('[data-calendar-day]').values())
			.filter((btn) => {
				const date = btn.dataset.date;
				if (!date) return false;

				// Filter out days we know are unavailable
				const availability = dayAvailabilitiesMap.get(date);
				return availability !== 'none' && !btn.classList.contains('timeslots__calendar-day--disabled');
			})
			.slice(0, 21);
		timeslotsListSection.innerHTML = loadingSpinner();

		let found = false;
		for (const calendarButton of calendarButtons) {
			const date = calendarButton.dataset.date;
			if (!date) continue;

			// Check cache first
			if (daySlotsMap.has(date)) {
				const cachedSlots = daySlotsMap.get(date)!;
				if (cachedSlots.length > 0) {
					selectCalendarDay(calendarButton);
					timeslotsListSection.innerHTML = renderTimeslots(date, cachedSlots);
					rebindTimeslotButtons();
					found = true;
					break;
				}
				continue;
			}

			// If not in cache, load from server
			const response = await callWp('GET', 'view_find_timeslot_fragment', { ...context.data, date });

			if (response.error) continue;

			daySlotsMap.set(date, response.timeslots);

			if (!response.timeslots || response.timeslots.length === 0) {
				calendarButton.classList.add('timeslots__calendar-day--unavailable');
				// TODO: re-enable
				// dayAvailabilitiesMap.set(date, 'none');
				continue;
			}

			selectCalendarDay(calendarButton);
			timeslotsListSection.innerHTML = renderTimeslots(date, response.timeslots);
			rebindTimeslotButtons();
			found = true;
			break;
		}

		if (!found) {
			timeslotsListSection.innerHTML =
				'<div class="spin__container">No available bookings found within the next few weeks. Please select another date or call your clinic to book.</div>';
		}

		timeslotsListSection.querySelector('[data-loading-spinner]')?.remove();
	};

	function selectCalendarDay(calendarButton: HTMLButtonElement) {
		// Update calendar month if needed
		if (calendarButton.closest('[data-calendar-month]')?.classList.contains('hidden')) {
			calendar?.querySelectorAll('[data-calendar-month]').forEach((month) => {
				month.classList.toggle('hidden', true);
			});
			calendarButton.closest('[data-calendar-month]')?.classList.toggle('hidden', false);
		}

		// Update selection
		calendarDays.forEach((btn) => {
			btn.classList.toggle('timeslots__calendar-day--selected', false);
			btn.disabled = false;
		});

		calendarButton.classList.toggle('timeslots__calendar-day--selected', true);
		calendarButton.disabled = true;
	}

	earliestTimeButton.addEventListener('click', async () => {
		loadEarliest();
	});

	//
	// Nurse / Therapist selection
	//
	const inputs = context.root.querySelectorAll<HTMLInputElement>('[data-therapist-select]');
	inputs.forEach((input) => {
		input.addEventListener('change', async () => {
			console.log('therapist changed');
			let therapistId: string | null = input.value;
			if (therapistId.length === 0) therapistId = null;

			const therapistWasNull = context.data.therapistId === null;
			context.data.therapistId = therapistId;

			// Clear both caches when therapist changes
			// dayAvailabilitiesMap.clear();
			// daySlotsMap.clear();

			// if (!context.data.date && therapistWasNull) {
			// 	loadEarliest();
			// 	return;
			// }

			if (context.data.therapistId && !dayAvailabilitiesMapWithNurses.has(context.data.therapistId)) {
				dayAvailabilitiesMapWithNurses.set(context.data.therapistId, new Map<string, DateAvailability>());
			}

			if (context.data.therapistId && !daySlotsMapWithNurses.has(context.data.therapistId)) {
				daySlotsMapWithNurses.set(context.data.therapistId, new Map<string, ApiBookingSlot[]>());
			}

			dayAvailabilitiesMap = dayAvailabilitiesMapWithNurses.get(context.data.therapistId ?? ALL_THERAPISTS_KEY);
			daySlotsMap = daySlotsMapWithNurses.get(context.data.therapistId ?? ALL_THERAPISTS_KEY);

			shouldStop = true;

			// TODO: refactor therapists..

			// Re-render the calendar buttons...

			console.log('swapping to therapist', therapistId);

			prefetchMonth();

			rerenderCalendarDays();
			rebindTimeslotButtons();
			// re-fetch the selected date
			loadSlotsForDate(calendar.querySelector<HTMLButtonElement>('.timeslots__calendar-day--selected')!);
		});
	});

	track({
		event: 'pageview',
		page_location: '/booking-online/schedule-visit/',
		page_title: 'Booking online - 3 - Schedule Visit',
	});
}

function reserveBooking(context: BookingContext) {
	return callWp('POST', 'reserve_booking', context.data);
}

function disableBackButton() {
	// history.pushState(null, document.title, location.href);
	// window.addEventListener('popstate', function (event) {
	// 	if (!this.confirm('You are about to leave the booking form. Are you want to cancel?')) {
	// 		history.pushState(null, document.title, location.href);
	// 	}
	// });
	// window.addEventListener('beforeunload', function (event) {
	// 	event.returnValue = 'You are about to leave the booking form. Are you want to cancel?';
	// });
}
function loadingSpinner() {
	return `<div class="spin__container" data-loading-spinner><div class="spin__spinner"></div></div>`;
}
